/**
 * AdminHelpConversation - uma conversa do chat de dúvidas no painel lateral do admin: falas com tela,
 * print e metadados da IA; resposta como equipe; "Promover a FAQ" a partir de uma resposta da equipe.
 */

import React, { useEffect, useRef, useState } from 'react';
import { BookPlus, X } from 'lucide-react';
import { cn, safeLocaleString } from '@/lib/utils';
import { ApiError } from '@/services/apiClient';
import { HelpMessageText, HelpTeamMessage } from '@/components/help/HelpMessageText';
import AdminHelpLoadError from '@/components/admin/help/AdminHelpLoadError';
import {
  createHelpNote,
  getHelpConversation,
  helpErrorMessage,
  replyHelpConversation,
  type HelpAdminMessage,
  type HelpAdminThread,
} from '@/services/adminHelpService';

interface AdminHelpConversationProps {
  conversationId: string;
  onClose: () => void;
  onReplied?: () => void;
}

type FaqDraft = { messageId: string; question: string; answer: string };

const MAX_TEXT = 2000;
const TEXTAREA_CLASS =
  'w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground disabled:opacity-60';
const PRIMARY_BUTTON =
  'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50';
const SECONDARY_BUTTON =
  'rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50';

function personLabel(user: HelpAdminThread['user']): string | null {
  const parts = [user?.display_name, user?.email].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Texto da última pergunta da pessoa antes da fala `index` (ou a última da conversa). */
function lastUserText(messages: HelpAdminMessage[], index: number): string {
  for (let i = index - 1; i >= 0; i--) if (messages[i].role === 'user') return messages[i].text;
  for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === 'user') return messages[i].text;
  return '';
}

const AdminHelpConversation: React.FC<AdminHelpConversationProps> = ({ conversationId, onClose, onReplied }) => {
  const [thread, setThread] = useState<HelpAdminThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  const [faq, setFaq] = useState<FaqDraft | null>(null);
  const [creatingFaq, setCreatingFaq] = useState(false);
  const [faqError, setFaqError] = useState<string | null>(null);
  const [promotedIds, setPromotedIds] = useState<string[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    setError(null);
    getHelpConversation(conversationId)
      .then((data) => {
        if (!stale) setThread(data);
      })
      .catch((err) => {
        if (stale) return;
        setError(err instanceof ApiError && err.status === 404 ? 'Conversa não encontrada.' : helpErrorMessage(err));
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [conversationId, reloadKey]);

  // Desce para a fala mais recente ao carregar e ao responder.
  const messageCount = thread?.messages.length ?? 0;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messageCount]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || sending) return;
    setSending(true);
    setReplyError(null);
    try {
      const { message } = await replyHelpConversation(conversationId, text);
      setThread((prev) => (prev ? { ...prev, messages: [...prev.messages, message] } : prev));
      setReply('');
      onReplied?.();
    } catch (err) {
      setReplyError(helpErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const startFaq = (index: number) => {
    if (!thread) return;
    const team = thread.messages[index];
    setFaq({ messageId: team.id, question: lastUserText(thread.messages, index), answer: team.text });
    setFaqError(null);
  };

  const createFaq = async () => {
    if (!faq || creatingFaq) return;
    const question = faq.question.trim();
    const answer = faq.answer.trim();
    if (!question || !answer) return;
    setCreatingFaq(true);
    setFaqError(null);
    try {
      await createHelpNote({ kind: 'faq', question, answer, expiresAt: null });
      setPromotedIds((ids) => [...ids, faq.messageId]);
      setFaq(null);
    } catch (err) {
      setFaqError(helpErrorMessage(err));
    } finally {
      setCreatingFaq(false);
    }
  };

  const person = thread ? personLabel(thread.user) : null;

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-start gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="break-words text-base font-semibold text-foreground">{thread?.conversation.title ?? 'Conversa'}</h2>
          {thread && (
            <>
              <p className={cn('truncate text-sm text-muted-foreground', !person && 'italic')}>{person ?? 'conta apagada'}</p>
              <p className="text-xs text-muted-foreground">
                Criada em {new Date(thread.conversation.created_at).toLocaleString('pt-BR')}
              </p>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          title="Fechar"
          className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {error ? (
          <AdminHelpLoadError message={error} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : loading || !thread ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Carregando conversa...</p>
        ) : thread.messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Conversa sem falas.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {thread.messages.map((m, i) => {
              if (m.role === 'user') {
                return (
                  <React.Fragment key={m.id}>
                    <UserMessage message={m} />
                  </React.Fragment>
                );
              }
              if (m.role === 'assistant') {
                return (
                  <React.Fragment key={m.id}>
                    <AssistantMessage message={m} />
                  </React.Fragment>
                );
              }
              return (
                <div key={m.id} className="flex w-full flex-col items-start gap-1">
                  <HelpTeamMessage text={m.text} />
                  {promotedIds.includes(m.id) ? (
                    <span className="pl-1 text-xs font-medium text-primary">FAQ criada</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startFaq(i)}
                      disabled={faq?.messageId === m.id}
                      className="inline-flex items-center gap-1 rounded px-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                    >
                      <BookPlus className="size-3.5" aria-hidden />
                      Promover a FAQ
                    </button>
                  )}
                  {faq?.messageId === m.id && (
                    <FaqForm
                      draft={faq}
                      busy={creatingFaq}
                      error={faqError}
                      onChange={setFaq}
                      onCancel={() => setFaq(null)}
                      onSubmit={createFaq}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Folga embaixo e botão à esquerda: a bolha do Crisp (54 px, a 20 px do canto inferior direito,
          z-index acima do painel) não pode cobrir o "Enviar". */}
      <footer className="shrink-0 space-y-2 border-t border-border px-4 pt-3 pb-[calc(5rem+env(safe-area-inset-bottom))]">
        <label htmlFor="help-team-reply" className="text-xs font-medium text-muted-foreground">
          Responder como equipe
        </label>
        <textarea
          id="help-team-reply"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          maxLength={MAX_TEXT}
          rows={3}
          placeholder="Responder como equipe"
          disabled={!thread}
          className={TEXTAREA_CLASS}
        />
        {replyError && <p className="break-words text-xs text-destructive">{replyError}</p>}
        <div className="flex items-center gap-3">
          <button type="button" onClick={sendReply} disabled={!thread || !reply.trim() || sending} className={PRIMARY_BUTTON}>
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
          <span className="text-xs text-muted-foreground">
            {reply.length}/{MAX_TEXT}
          </span>
        </div>
      </footer>
    </div>
  );
};

function UserMessage({ message }: { message: HelpAdminMessage }) {
  return (
    <div className="flex max-w-[85%] flex-col items-end gap-1 self-end">
      {message.text && (
        <div className="whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-sm leading-relaxed text-primary-foreground">
          {message.text}
        </div>
      )}
      {message.image_url && (
        <a
          href={message.image_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-end gap-1 text-xs text-primary hover:underline"
        >
          <img
            src={message.image_url}
            alt="Print enviado pela pessoa"
            className="max-h-28 max-w-full rounded-lg border border-border object-cover"
          />
          ver print
        </a>
      )}
      {message.screen && <span className="break-all text-[11px] text-muted-foreground">tela: {message.screen}</span>}
    </div>
  );
}

function AssistantMessage({ message }: { message: HelpAdminMessage }) {
  const meta: { text: string; alert?: boolean }[] = [];
  if (message.knew !== null && message.knew !== undefined) {
    meta.push(message.knew ? { text: 'soube' } : { text: 'não soube', alert: true });
  }
  if (message.topic) meta.push({ text: message.topic });
  if (message.model) meta.push({ text: message.model });
  if (message.cost_usd !== null && message.cost_usd !== undefined) {
    const cost = safeLocaleString(Number(message.cost_usd), 'pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    meta.push({ text: `US$ ${cost}` });
  }
  if (message.feedback === 1) meta.push({ text: '👍' });
  if (message.feedback === -1) meta.push({ text: '👎', alert: true });
  if (message.needs_team) meta.push({ text: 'precisa da equipe', alert: true });

  return (
    <div className="flex max-w-[92%] flex-col gap-1 self-start">
      <div className="break-words rounded-2xl rounded-bl-md bg-muted px-3.5 py-2.5 text-sm leading-relaxed text-foreground">
        <HelpMessageText text={message.text} />
      </div>
      {meta.length > 0 && (
        <p className="break-words pl-1 text-[11px] text-muted-foreground">
          {meta.map((item, i) => (
            <React.Fragment key={i}>
              {i > 0 && ' · '}
              <span className={cn(item.alert && 'font-medium text-destructive')}>{item.text}</span>
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
}

function FaqForm({
  draft,
  busy,
  error,
  onChange,
  onCancel,
  onSubmit,
}: {
  draft: FaqDraft;
  busy: boolean;
  error: string | null;
  onChange: (draft: FaqDraft) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="mt-1 w-full space-y-2 rounded-lg border border-border bg-card p-3">
      <div className="text-xs font-semibold text-foreground">Nova FAQ</div>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Pergunta</span>
        <textarea
          value={draft.question}
          onChange={(e) => onChange({ ...draft, question: e.target.value })}
          maxLength={MAX_TEXT}
          rows={2}
          className={TEXTAREA_CLASS}
        />
      </label>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Resposta</span>
        <textarea
          value={draft.answer}
          onChange={(e) => onChange({ ...draft, answer: e.target.value })}
          maxLength={MAX_TEXT}
          rows={4}
          className={TEXTAREA_CLASS}
        />
      </label>
      {error && <p className="break-words text-xs text-destructive">{error}</p>}
      {/* À esquerda, como o "Enviar": longe do canto onde flutua a bolha do Crisp. */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy || !draft.question.trim() || !draft.answer.trim()}
          className={PRIMARY_BUTTON}
        >
          {busy ? 'Criando...' : 'Criar FAQ'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={SECONDARY_BUTTON}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default AdminHelpConversation;
